import { router } from 'expo-router';
import { ReactNode } from 'react';
import {
  ActivityIndicator,
  StyleSheet,
  TouchableOpacity,
  TouchableOpacityProps,
  TouchableWithoutFeedback,
  View,
} from 'react-native';

import { useMenuClickEvents } from '@fhs/menu/src/analytics';
import { IconShoppingBag, Text, tokens } from '@fhs/ui';
import { useOrderContext } from '@fhs-legacy/frontend/src/state/order';

export type HeaderCartButtonProps = Omit<TouchableOpacityProps, 'children'>;
const ShoppingBagContainer = ({
  children,
  isDisabled,
  ctaDisabledEvent,
  headerCartProps,
  accessibilityLabel,
  accessibilityValueText,
}: {
  children: ReactNode;
  isDisabled: boolean;
  ctaDisabledEvent: () => void;
  headerCartProps?: HeaderCartButtonProps;
  accessibilityLabel: string;
  accessibilityValueText: string;
}) => {
  if (isDisabled) {
    return (
      <TouchableWithoutFeedback
        onPress={() => {
          ctaDisabledEvent();
        }}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
        accessibilityValue={{ text: accessibilityValueText }}
        aria-disabled={true}
      >
        <View style={styles.container}>{children}</View>
      </TouchableWithoutFeedback>
    );
  }
  return (
    <TouchableOpacity
      style={styles.container}
      {...headerCartProps}
      accessibilityLabel={accessibilityLabel}
      accessibilityValue={{ text: accessibilityValueText }}
      onPress={() => {
        router.navigate('/cart');
      }}
    >
      {children}
    </TouchableOpacity>
  );
};
export function HeaderCartButton(props: HeaderCartButtonProps) {
  const {
    numCartPreviewEntries,
    addItemToCartLoading,
    reorder: { loadingEnrichCartEntries },
  } = useOrderContext();

  const isLoading = addItemToCartLoading || loadingEnrichCartEntries;
  const isCartEmpty = numCartPreviewEntries < 1;
  const { logEmptyCartCTAClick } = useMenuClickEvents();
  const cartCountText = `${numCartPreviewEntries} ${numCartPreviewEntries === 1 ? 'item' : 'items'}`;
  const cartAccessibilityLabel = isLoading
    ? `Cart, updating. ${cartCountText}`
    : `Cart, ${cartCountText}`;

  return (
    <ShoppingBagContainer
      isDisabled={isCartEmpty || addItemToCartLoading}
      ctaDisabledEvent={logEmptyCartCTAClick}
      headerCartProps={props}
      accessibilityLabel={cartAccessibilityLabel}
      accessibilityValueText={cartCountText}
    >
      <View>
        <IconShoppingBag size={32} aria-hidden />
      </View>
      <View
        style={[
          styles.badgeRounded,
          {
            backgroundColor: isCartEmpty
              ? tokens.colors.$houseLight
              : tokens.colors.$houseRed,
          },
        ]}
      >
        <Text.Paragraph
          style={{
            color: isCartEmpty ? tokens.colors.$black : tokens.colors.$white,
            justifyContent: 'center',
            alignContent: 'center',
          }}
          size="xs"
          weight="bold"
        >
          {isLoading ? (
            <ActivityIndicator
              color={isCartEmpty ? tokens.colors.$houseRed : 'white'}
              size="small"
              style={{ transform: [{ scale: 0.5 }], paddingTop: 1 }}
            />
          ) : (
            numCartPreviewEntries
          )}
        </Text.Paragraph>
      </View>
    </ShoppingBagContainer>
  );
}

const styles = StyleSheet.create({
  container: {
    alignItems: 'flex-end',
    position: 'relative',
  },
  badgeRounded: {
    width: 20,
    height: 20,
    borderRadius: 100,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    top: -10,
    right: -10,
  },
});
